/**
 * TabBar — Horizontal tab navigation for the notes window.
 *
 * Features:
 *  - Default tabs (pinned, cannot delete)
 *  - Custom tabs with right-click context menu
 *  - [+] button to add new custom tab
 *  - Active tab indicator
 */
import React, { useState, useRef, useCallback } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { Add, PushPin } from '@mui/icons-material';
import TabContextMenu from './TabContextMenu';

export default function TabBar({
    tabs = [],
    activeTabId,
    onSelectTab,
    onAddTab,
    onDeleteTab,
    onRenameTab,
}) {
    const [contextMenu, setContextMenu] = useState(null); // { x, y, tab }
    const [editingTabId, setEditingTabId] = useState(null);
    const [editValue, setEditValue] = useState('');
    const editRef = useRef(null);

    const handleContextMenu = useCallback((e, tab) => {
        e.preventDefault();
        setContextMenu({ x: e.clientX, y: e.clientY, tab });
    }, []);

    const closeContextMenu = useCallback(() => setContextMenu(null), []);

    /* ── Inline rename ── */
    const startRename = useCallback((tab) => {
        setEditingTabId(tab.tabId);
        setEditValue(tab.tabName);
        closeContextMenu();
        setTimeout(() => editRef.current?.focus(), 50);
    }, [closeContextMenu]);

    const commitRename = useCallback(() => {
        if (editingTabId && editValue.trim()) {
            onRenameTab?.(editingTabId, editValue.trim());
        }
        setEditingTabId(null);
    }, [editingTabId, editValue, onRenameTab]);

    const handleRenameKey = useCallback((e) => {
        if (e.key === 'Enter') commitRename();
        if (e.key === 'Escape') setEditingTabId(null);
    }, [commitRename]);

    /* ── Add tab ── */
    const handleAddTab = useCallback(() => {
        const name = prompt('Tab name:');
        if (name?.trim()) onAddTab?.(name.trim());
    }, [onAddTab]);

    return (
        <div className="nw-tabbar">
            <div className="nw-tabbar-scroll">
                {tabs.map((tab) => {
                    const isActive = tab.tabId === activeTabId;

                    return (
                        <div
                            key={tab.tabId}
                            className={`nw-tab ${isActive ? 'nw-tab--active' : ''}`}
                            onClick={() => onSelectTab?.(tab.tabId)}
                            onContextMenu={(e) => handleContextMenu(e, tab)}
                            title={tab.tabName}
                        >
                            {tab.isPinned && (
                                <PushPin sx={{ fontSize: 11, opacity: 0.4, mr: 0.3 }} />
                            )}
                            {editingTabId === tab.tabId ? (
                                <input
                                    ref={editRef}
                                    className="nw-tab-rename-input"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={commitRename}
                                    onKeyDown={handleRenameKey}
                                />
                            ) : (
                                <span className="nw-tab-label">{tab.tabName}</span>
                            )}
                            {tab.content?.plainText?.trim() && (
                                <span className="nw-tab-dot" />
                            )}
                        </div>
                    );
                })}

                {/* ── Add Tab button ── */}
                <Tooltip title="Add custom tab" arrow>
                    <IconButton
                        size="small"
                        onClick={handleAddTab}
                        sx={{
                            color: 'var(--muted)',
                            ml: 0.5,
                            width: 28,
                            height: 28,
                            '&:hover': { color: 'var(--brand)', backgroundColor: 'var(--bg-3)' },
                        }}
                    >
                        <Add sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
            </div>

            {/* ── Context menu ── */}
            {contextMenu && (
                <TabContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    tab={contextMenu.tab}
                    onClose={closeContextMenu}
                    onRename={() => startRename(contextMenu.tab)}
                    onDelete={() => {
                        onDeleteTab?.(contextMenu.tab.tabId);
                        closeContextMenu();
                    }}
                />
            )}
        </div>
    );
}
